import { z } from 'zod';

/**
 * Appointment waitlist entries as a connector reports and accepts them
 * (ADR-271). EHR-native ids, EHR-neutral vocabulary: a connector translates its
 * EHR's own encoding (athena's 1=Sunday day ids, JSON-string arrays, LOW/NORMAL/
 * HIGH) at its edge.
 */

export const ehrWaitlistPrioritySchema = z.enum(['low', 'normal', 'high']);
export type EhrWaitlistPriority = z.infer<typeof ehrWaitlistPrioritySchema>;

/**
 * What a waitlist entry asks for. A null provider means any provider. The
 * location (athena department) is where the entry is filed; `anyLocation` says
 * any location is acceptable. Days are 0=Sunday..6=Saturday, empty = any day.
 * `hourFrom`/`hourTo` bound the hour an appointment should START in, as a
 * window [hourFrom, hourTo) on the 24-hour clock, practice-local.
 */
export const ehrWaitlistRequestSchema = z.object({
  ehrLocationId: z.string(),
  anyLocation: z.boolean(),
  ehrProviderId: z.string().nullable(),
  ehrAppointmentTypeId: z.string().nullable(),
  /** The booked appointment this entry would replace, if any. */
  ehrAppointmentId: z.string().nullable(),
  daysOfWeek: z.array(z.number().int().min(0).max(6)),
  hourFrom: z.number().int().min(0).max(24).nullable(),
  hourTo: z.number().int().min(0).max(24).nullable(),
  /** Always set on a write: athena can't clear an entry's priority. */
  priority: ehrWaitlistPrioritySchema,
  note: z.string().nullable(),
});
export type EhrWaitlistRequest = z.infer<typeof ehrWaitlistRequestSchema>;

/** Create input: who is waiting, plus what they ask for. */
export const ehrWaitlistCreateSchema = ehrWaitlistRequestSchema.extend({
  ehrPatientId: z.string(),
});
export type EhrWaitlistCreate = z.infer<typeof ehrWaitlistCreateSchema>;

/**
 * An entry as the EHR holds it. A null `ehrLocationId` means any location (athena
 * drops the department from an entry filed with `allowanydepartment`).
 * `createdAt` is ISO 8601 with offset, or null when the EHR's timestamp couldn't
 * be placed in a timezone.
 */
export const ehrWaitlistEntrySchema = ehrWaitlistRequestSchema.omit({ anyLocation: true }).extend({
  ehrWaitlistId: z.string(),
  ehrPatientId: z.string(),
  ehrLocationId: z.string().nullable(),
  /** Null when the EHR reports none (older entries may have none). */
  priority: ehrWaitlistPrioritySchema.nullable(),
  createdAt: z.string().nullable(),
});
export type EhrWaitlistEntry = z.infer<typeof ehrWaitlistEntrySchema>;

/** Every entry the practice holds, read in full (a partial read fails instead). */
export const ehrWaitlistListResponseSchema = z.object({
  entries: z.array(ehrWaitlistEntrySchema),
});
export type EhrWaitlistListResponse = z.infer<typeof ehrWaitlistListResponseSchema>;

/**
 * One entry by id, with an explicit outcome (P6): `not_found` means the EHR has
 * no entry with that id — it was deleted there. Every other failure is an error.
 */
export const ehrWaitlistEntryResponseSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('found'), entry: ehrWaitlistEntrySchema }),
  z.object({ status: z.literal('not_found') }),
]);
export type EhrWaitlistEntryResponse = z.infer<typeof ehrWaitlistEntryResponseSchema>;

export const ehrWaitlistCreateResponseSchema = z.object({
  ehrWaitlistId: z.string(),
});
export type EhrWaitlistCreateResponse = z.infer<typeof ehrWaitlistCreateResponseSchema>;
